/**
 * GroupSpaceManagementRepository
 * 그룹 공간 관리 API 호출 레이어
 */
import { API_BASE_URL } from '../../../config/api';
import type { GroupSpace, Reservation } from '../models/GroupSpaceManagement';

/**
 * GroupSpaceManagementRepository 클래스
 * 그룹 공간 및 예약 관련 API 호출 담당
 */
export class GroupSpaceManagementRepository {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * 인증 헤더 생성
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * 그룹 공간 목록 조회
   */
  async getGroupSpaces(): Promise<GroupSpace[]> {
    const response = await fetch(`/v1/owner/group-spaces`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('그룹 공간 목록을 불러오는데 실패했습니다.');
    }

    const data = await response.json();
    return data.spaces || [];
  }

  /**
   * 그룹 공간 상세 조회
   */
  async getGroupSpaceById(spaceId: string): Promise<GroupSpace> {
    const response = await fetch(`/v1/owner/group-spaces/${spaceId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('그룹 공간 정보를 불러오는데 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 그룹 공간 생성
   */
  async createGroupSpace(spaceData: Partial<GroupSpace>): Promise<GroupSpace> {
    const response = await fetch(`/v1/owner/group-spaces`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(spaceData),
    });

    if (!response.ok) {
      throw new Error('그룹 공간 생성에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 그룹 공간 수정
   */
  async updateGroupSpace(
    spaceId: string,
    spaceData: Partial<GroupSpace>
  ): Promise<GroupSpace> {
    const response = await fetch(`/v1/owner/group-spaces/${spaceId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(spaceData),
    });

    if (!response.ok) {
      throw new Error('그룹 공간 수정에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 그룹 공간 삭제
   */
  async deleteGroupSpace(spaceId: string): Promise<void> {
    const response = await fetch(`/v1/owner/group-spaces/${spaceId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('그룹 공간 삭제에 실패했습니다.');
    }
  }

  /**
   * 예약 목록 조회
   */
  async getReservations(): Promise<Reservation[]> {
    const response = await fetch(`/v1/owner/group-reservations`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('예약 목록을 불러오는데 실패했습니다.');
    }

    const data = await response.json();
    return data.reservations || [];
  }

  /**
   * 예약 상세 조회
   */
  async getReservationById(reservationId: string): Promise<Reservation> {
    const response = await fetch(
      `/v1/owner/group-reservations/${reservationId}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('예약 정보를 불러오는데 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 예약 생성
   */
  async createReservation(reservationData: Partial<Reservation>): Promise<Reservation> {
    const response = await fetch(`/v1/owner/group-reservations`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(reservationData),
    });

    if (!response.ok) {
      throw new Error('예약 생성에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 예약 수정
   */
  async updateReservation(
    reservationId: string,
    reservationData: Partial<Reservation>
  ): Promise<Reservation> {
    const response = await fetch(
      `/v1/owner/group-reservations/${reservationId}`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(reservationData),
      }
    );

    if (!response.ok) {
      throw new Error('예약 수정에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 예약 취소
   */
  async cancelReservation(reservationId: string): Promise<void> {
    const response = await fetch(
      `/v1/owner/group-reservations/${reservationId}/cancel`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('예약 취소에 실패했습니다.');
    }
  }
}

/**
 * GroupSpaceManagementRepository 싱글톤 인스턴스
 */
export const groupSpaceManagementRepository = new GroupSpaceManagementRepository();
